import React from "react";
import styled, { keyframes } from "styled-components";
import { useDropzone } from "react-dropzone";
import * as faceapi from "face-api.js";
import html2canvas from "html2canvas";

const Container = styled.div`
  padding: 40px 80px;
  @media screen and (max-width: 767px) {
    padding: 16px;
  }
`;
const Canvas = styled.div`
  text-align: center;
  margin-bottom: 40px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
  @media screen and (max-width: 767px) {
    canvas {
      width: calc(600px / 2) !important;
      height: calc(320px / 2) !important;
    }
  }
`;
const Dropzone = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 180px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 0 24px;
  cursor: pointer;
  text-align: center;
`;
const DropzoneText = styled.div``;
const PreviewWrap = styled.div`
  display: flex;
  align-items: center;
  width: 600px;
`;
const Preview = styled.div`
  min-width: 300px;
  width: 300px;
  height: 320px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
`;
const PreviewTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 300px;
  height: 320px;
  text-align: center;
`;
const PreviewText = styled.div`
  font-size: 48px;
  font-weight: bold;
`;
const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  padding: 80px 0;
`;
const LoadingSpin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;
const LoadingIcon = styled.div`
  animation: ${LoadingSpin} infinite 1s linear;
`;
const LoadingText = styled.div`
  font-weight: bold;
  margin-left: 8px;
`;
const Score = styled.div`
  text-align: center;
  margin-bottom: 16px;
`;
const Download = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

export const Uploader: React.FC = () => {
  const [preview, setPreview] = React.useState("");
  const [donwloadUrl, setDonwloadUrl] = React.useState("");
  const [pienScore, setPienScore] = React.useState(0);
  const [pienFace, setPienFace] = React.useState({
    emoji: "",
    text: "",
  });
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const onDrop = React.useCallback((acceptedFiles) => {
    setIsLoaded(false);
    setIsLoading(true);
    setPreview(URL.createObjectURL(acceptedFiles[0]));
  }, []);
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: "image/jpeg, image/png",
  });

  React.useEffect(() => {
    if (!preview.length) {
      return;
    }
    const inputEl = document.getElementById("input") as HTMLImageElement;
    const previewEl = document.getElementById("preview") as HTMLDivElement;
    const canvasEl = document.getElementById("canvas") as HTMLDivElement;
    (async () => {
      const MODEL_URL = `${process.env.PUBLIC_URL}/models`;
      await faceapi.nets.tinyFaceDetector.load(MODEL_URL);
      await faceapi.nets.faceExpressionNet.load(MODEL_URL);

      const detectionsWithExpressions = await faceapi
        .detectAllFaces(inputEl, new faceapi.TinyFaceDetectorOptions())
        .withFaceExpressions();

      const score =
        detectionsWithExpressions.length > 0
          ? detectionsWithExpressions[0].expressions.sad
          : -1;

      setPienScore(score);

      if (score > 0.9) {
        setPienFace({
          emoji: "🐘",
          text: "ぴえん<br>超えて<br>ぱおん",
        });
      } else if (score > 0.7) {
        setPienFace({
          emoji: "🥺",
          text: "ぴえん",
        });
      } else if (score > 0.5) {
        setPienFace({
          emoji: "🥺",
          text: "ぴえんかも",
        });
      } else {
        setPienFace({
          emoji: "",
          text: "ぴえん<br>じゃない",
        });
      }

      html2canvas(previewEl, {
        allowTaint: true,
      }).then((canvas) => {
        const prevCanvasEl = canvasEl.querySelector("canvas");
        if (prevCanvasEl) {
          canvasEl.removeChild(prevCanvasEl);
        }
        canvasEl.appendChild(canvas);
        setDonwloadUrl(canvas.toDataURL("image/png"));
        setIsLoaded(true);
        setIsLoading(false);
      });
    })();
  }, [preview]);

  return (
    <Container>
      {isLoading && (
        <Loading>
          <LoadingIcon>🥺</LoadingIcon>
          <LoadingText>判定中...</LoadingText>
        </Loading>
      )}
      <Canvas id="canvas" style={{ display: isLoaded ? "block" : "none" }} />
      <PreviewWrap
        id="preview"
        style={{ position: "absolute", top: "-100%", left: "-100%" }}
      >
        <img id="input" src={preview} alt="" style={{ display: "none" }} />
        <Preview style={{ backgroundImage: `url(${preview})` }} />
        <PreviewTextWrap>
          <PreviewText>
            <span dangerouslySetInnerHTML={{ __html: pienFace.text }} />
            {pienFace.emoji}
          </PreviewText>
        </PreviewTextWrap>
      </PreviewWrap>
      {isLoaded && (
        <>
          <Score>
            {pienScore > -1
              ? `ぴえんスコア: ${Math.floor(pienScore * 100)}`
              : "エラー: 顔が見つからないよ🥺"}
          </Score>
          {donwloadUrl.length > 0 && (
            <Download>
              <a href={donwloadUrl} download>
                画像をダウンロードする
              </a>
            </Download>
          )}
        </>
      )}
      <Dropzone {...getRootProps()}>
        <input {...getInputProps()} />
        <DropzoneText>
          タップするか、ここに画像ファイルをドラッグ&amp;ドロップしてね
          <br />
          ※画像はサーバに保存されません
        </DropzoneText>
      </Dropzone>
    </Container>
  );
};
